import { useCallback, useEffect, useState } from "react";
import { uuidv7 } from "../../../core/platform/id";
import { formatPesos, parsePesos, MoneyInputError } from "../money";
import type { BooksApi, Place, PlaceId, Position as PositionBody } from "./api";

/**
 * Where the café's money is (T021, User Story 2).
 *
 * Every figure here is derived from the till's own records — no balance was
 * ever typed in. Counting a place records the difference as its own entry with
 * a reason, so a shortfall is explained rather than absorbed.
 */

export interface PositionProps {
  api: BooksApi;
  version: number;
  onChanged(): void;
}

type Form = { kind: "count"; place: Place } | { kind: "transfer" } | null;

export function Position({ api, version, onChanged }: PositionProps) {
  const [body, setBody] = useState<PositionBody | null>(null);
  const [form, setForm] = useState<Form>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBody(await api.position());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the position");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const done = (message: string) => {
    setForm(null);
    setNotice(message);
    onChanged();
    void load();
  };

  if (form?.kind === "count") {
    return <CountForm api={api} place={form.place} onDone={done} onCancel={() => setForm(null)} />;
  }
  if (form?.kind === "transfer" && body) {
    return <TransferForm api={api} places={body.places} onDone={done} onCancel={() => setForm(null)} />;
  }

  return (
    <section className="books__panel">
      {error && (
        <p className="books__error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="books__notice" role="status">
          {notice}
        </p>
      )}

      {!body ? (
        <p className="books__hint">Loading…</p>
      ) : (
        <>
          <ul className="books__list">
            {body.places.map((place) => (
              <li key={place.id} className="books__row">
                <span className="books__row-name">{place.name}</span>
                <span className="books__figure">₱{formatPesos(place.balance)}</span>
                <button type="button" className="books__action" onClick={() => setForm({ kind: "count", place })}>
                  Count
                </button>
                {place.uncountedShifts > 0 && (
                  <span className="books__row-meta">
                    {place.uncountedShifts} shift{place.uncountedShifts === 1 ? "" : "s"} closed without a drawer
                    count — their expected cash stands in
                  </span>
                )}
                {place.lastCount && (
                  <span className="books__row-meta">
                    Last counted {new Date(place.lastCount.at).toLocaleDateString()}
                    {place.lastCount.difference === 0
                      ? " — it matched"
                      : `, out by ₱${formatPesos(place.lastCount.difference)}`}
                  </span>
                )}
              </li>
            ))}
          </ul>

          <p className="books__total">
            All together <strong>₱{formatPesos(body.total)}</strong>
          </p>

          {body.waitingToCategorise.count > 0 && (
            <p className="books__hint">
              {body.waitingToCategorise.count} cash-out
              {body.waitingToCategorise.count === 1 ? "" : "s"} from the till (₱
              {formatPesos(body.waitingToCategorise.amount)}) still need a category — see Spending.
            </p>
          )}

          <div className="books__buttons">
            <button type="button" className="books__action" onClick={() => setForm({ kind: "transfer" })}>
              Move money
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function useAmount() {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const read = (allowZero = false): number | null => {
    try {
      const centavos = parsePesos(text);
      if (centavos === 0 && !allowZero) {
        setError("Enter an amount.");
        return null;
      }
      return centavos;
    } catch (caught) {
      setError(caught instanceof MoneyInputError ? caught.message : "That is not an amount.");
      return null;
    }
  };
  return { text, setText, error, setError, read };
}

interface FormProps {
  api: BooksApi;
  onDone(message: string): void;
  onCancel(): void;
}

function CountForm({ api, place, onDone, onCancel }: FormProps & { place: Place }) {
  const amount = useAmount();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [opId] = useState(() => uuidv7());

  const submit = async () => {
    amount.setError(null);
    const counted = amount.read(true);
    if (counted === null) return;
    if (reason.trim() === "") {
      amount.setError("Say why it was counted.");
      return;
    }

    setBusy(true);
    try {
      const { difference } = await api.count({ opId, place: place.id, counted, reason: reason.trim() });
      onDone(
        difference === 0
          ? `${place.name} counted — it matched the books.`
          : `${place.name} counted. It was ₱${formatPesos(Math.abs(difference))} ${difference < 0 ? "short" : "over"}, now corrected.`,
      );
    } catch (caught) {
      amount.setError(caught instanceof Error ? caught.message : "Could not record it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books__panel">
      <h3 className="books__subhead">Count {place.name}</h3>
      <p className="books__hint">
        The books say ₱{formatPesos(place.balance)}. Enter what is really there; the difference is
        recorded, not hidden.
      </p>

      <label className="books__field">
        <span>Counted (pesos)</span>
        <input value={amount.text} onChange={(e) => amount.setText(e.target.value)} inputMode="decimal" autoFocus />
      </label>
      <label className="books__field">
        <span>Why</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Weekly check" maxLength={200} />
      </label>

      {amount.error && (
        <p className="books__error" role="alert">
          {amount.error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          Record count
        </button>
      </div>
    </section>
  );
}

function TransferForm({ api, places, onDone, onCancel }: FormProps & { places: Place[] }) {
  const movable = places.filter((place) => place.id !== "drawer");
  const amount = useAmount();
  const [from, setFrom] = useState<PlaceId>(movable[0]?.id ?? "cash_held");
  const [to, setTo] = useState<PlaceId>(movable[1]?.id ?? "capital_fund");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [opId] = useState(() => uuidv7());

  const submit = async () => {
    amount.setError(null);
    const centavos = amount.read();
    if (centavos === null) return;
    if (reason.trim() === "") {
      amount.setError("Say why the money moved.");
      return;
    }

    setBusy(true);
    try {
      await api.transfer({ opId, from, to, amount: centavos, reason: reason.trim() });
      onDone(`₱${formatPesos(centavos)} moved.`);
    } catch (caught) {
      amount.setError(caught instanceof Error ? caught.message : "Could not move it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="books__panel">
      <h3 className="books__subhead">Move money</h3>
      <p className="books__hint">
        The drawer is not here: its money moves when a shift closes, or through Cash out at the till.
      </p>

      <div className="books__pickers">
        <label className="books__field">
          <span>From</span>
          <select value={from} onChange={(e) => setFrom(e.target.value as PlaceId)}>
            {movable.map((place) => (
              <option key={place.id} value={place.id}>
                {place.name} (₱{formatPesos(place.balance)})
              </option>
            ))}
          </select>
        </label>
        <label className="books__field">
          <span>To</span>
          <select value={to} onChange={(e) => setTo(e.target.value as PlaceId)}>
            {movable.map((place) => (
              <option key={place.id} value={place.id}>
                {place.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="books__field">
        <span>How much (pesos)</span>
        <input value={amount.text} onChange={(e) => amount.setText(e.target.value)} inputMode="decimal" autoFocus />
      </label>
      <label className="books__field">
        <span>Why</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Deposited at the bank" maxLength={200} />
      </label>

      {amount.error && (
        <p className="books__error" role="alert">
          {amount.error}
        </p>
      )}

      <div className="books__buttons">
        <button type="button" className="books__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="books__action is-primary" onClick={() => void submit()} disabled={busy}>
          Move
        </button>
      </div>
    </section>
  );
}
